<script setup lang="ts">
import { ref } from "vue";
import type { MetaObject } from "@unhead/vue";

import {
  useRoute,
  useRuntimeConfig,
  useFetch,
  useHead,
  createError,
} from "#imports";
import type { RouteLocationNormalizedLoaded } from "vue-router";

interface MetaTag {
  hid?: string;
  name?: string;
  content?: string;
}

interface Header {
  img?: string;
  title?: string;
  text?: string;
}

interface CaseStudyAboutProject {
  image_safari_ext?: string;
  image?: string;
  about_industry?: string;
  alt?: string;
  text_1?: string;
  text_2?: string;
}

interface IconCard {
  image_safari_ext?: string;
  image?: string;
  alt?: string;
  heading?: string;
  subheading?: string;
}

interface CaseStudyFeatures {
  heading?: string;
  subheading?: string;
  admin_icon_card_content?: IconCard[];
  user_icon_card_content?: IconCard[];
}

export interface PageContent {
  meta_tags?: {
    title?: string;
    meta?: MetaTag[];
  };
  header?: {
    img?: string;
    title?: string;
    text?: string;
  };
  case_study_about_project?: CaseStudyAboutProject;
  case_study_features_one?: CaseStudyFeatures;
  case_study_features_two?: CaseStudyFeatures;
}

type RouteParams = { slug: string };

const route = useRoute() as RouteLocationNormalizedLoaded & {
  params: RouteParams;
};
const slug = route.params.slug;

const url = useRequestURL();
const { data, error } = await useFetch(
  `${url.origin}/content/services/industries/${slug}-content.json`
);

if (error.value || !data.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Page not found",
  });
}

// ✅ Now data.value is strongly typed
useHead({
  title: data.value?.meta_tags?.title ?? "",
  meta: data.value?.meta_tags?.meta as MetaObject["meta"],
});

const visible = ref(false);
const openModal = () => {
  visible.value = true;
};
</script>

<template>
  <section class="bg-transparent text-center pb-16 lg:px-4">
    <div class="max-w-screen-xl mx-auto px-4 lg:pt-10 lg:pb-8">
      <h1 class="text-3xl lg:text-5xl font-bold relative">
        <WrGradientText appendClass="leading-[1.3]">{{
          data.header.title
        }}</WrGradientText>
      </h1>
      <p
        class="md:text-lg tracking-[0.5px] lg:px-32"
        v-html="data.header.text"
      ></p>
    </div>
  </section>

  <section class="py-10 lg:pb-20">
    <div class="grid lg:grid-cols-2 items-center max-w-screen-xl mx-auto px-4">
      <NuxtImg
        :src="data.case_study_about_project.image"
        :alt="data.case_study_about_project.alt"
        width="565"
        class="mx-auto h-full rounded-md"
        loading="eager"
        format="webp"
      />

      <div class="text-left max-lg:mt-4">
        <h2 class="font-bold">
          <WrGradientText
            append-class="pt-0"
            :text="data.case_study_about_project.about_industry"
          ></WrGradientText>
        </h2>

        <div
          v-if="data.case_study_about_project.text_1"
          class="!leading-[1.8] tracking-[0.5px] mb-4"
          v-html="data.case_study_about_project.text_1"
        ></div>

        <div
          v-if="data.case_study_about_project.text_2"
          class="!leading-[1.8] tracking-[0.5px] mb-4"
          v-html="data.case_study_about_project.text_2"
        ></div>
      </div>
    </div>
  </section>

  <section class="py-6 lg:py-12">
    <div class="px-4 max-w-screen-xl mx-auto lg:text-center">
      <h2 class="font-bold">
        <WrGradientText
          :text="data.case_study_features_one.heading"
        ></WrGradientText>
      </h2>
      <p
        v-if="data.case_study_features_one.subheading"
        v-html="data.case_study_features_one.subheading"
        class="tracking-[0.5px] mb-10 mx-auto lg:px-20"
      ></p>

      <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <CardFunctionality
          v-for="(item, i) in data.case_study_features_one
            .admin_icon_card_content"
          :key="i"
          :img="item.image"
          :title="item.heading"
          :description="item.subheading"
        />
      </div>
    </div>
  </section>

  <section v-if="data.case_study_features_two" class="py-6 lg:py-12">
    <div class="px-4 max-w-screen-xl mx-auto lg:text-center">
      <h2 class="font-bold">
        <WrGradientText
          :text="data.case_study_features_two.heading"
        ></WrGradientText>
      </h2>
      <p
        v-if="data.case_study_features_two.subheading"
        v-html="data.case_study_features_two.subheading"
        class="tracking-[0.5px] mb-10 mx-auto lg:px-20"
      ></p>

      <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <CardFunctionality
          v-for="(item, i) in data.case_study_features_two
            .user_icon_card_content"
          :key="i"
          :img="item.image"
          :title="item.heading"
          :description="item.subheading"
        />
      </div>
    </div>
  </section>

  <section class="relative z-10 py-10 lg:pb-28 overflow-visible order-9">
    <div class="max-w-screen-lg mx-auto px-4 w-full relative">
      <div
        class="hidden dark:block opacity-30 md:size-[764px] rounded-full bg-[linear-gradient(136.28deg,_rgba(51,4,79,0)_30.43%,_#260049_54.53%,_#36006F_92.37%)] absolute -top-[30%] -left-[67%]"
      ></div>

      <div
        class="hidden dark:block opacity-50 size-[190px] rounded-full bg-[image:var(--bg-circle-top-light)] absolute -bottom-[15%] right-0 top-0"
      ></div>
      <WrSelectedProject />
    </div>
  </section>

  <section class="mt-4 relative z-10 max-w-screen-xl mx-auto px-4">
    <h2 class="text-center">
      <WrGradientText text="Trusted for excellence and efficiency" />
      <p class="!leading-[1.8] text-lg text-[var(--foreground)]">
        WebReinvent is trusted by leading organisations to enhance their
        development and operational efficiency.
      </p>
    </h2>
    <WrTestimonialCardCarousel class="mt-12" />
    <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7"
      >View All</WrButton
    >
  </section>
</template>
